import React, { useMemo, forwardRef } from "react";
import "./AuctionDateInput.css";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";

/**
 * RESTRUCTURE 1: Use React.forwardRef for CustomInput.
 * This is essential for react-datepicker to correctly pass its ref
 * to the <input> element, which it needs to manage focus and clicks.
 */
const CustomInput = forwardRef(
  (
    { value, onClick, placeholder, readOnly, elemHeight, disabled },
    ref // This ref is passed down from DatePicker
  ) => (
    <div
      className={`custom-input ${disabled ? "disabled-input" : ""}`}
      style={{ height: `${elemHeight || "48"}px` }}
    >
      <div
        className="input-icon-wrapper"
        onClick={!disabled ? onClick : undefined}
      >
        <input
          ref={ref} // Attach the forwarded ref here
          type="text"
          readOnly={readOnly || disabled}
          value={value}
          placeholder={placeholder}
          className="input-with-icon"
          style={{ textAlign: value ? "center" : "left" }}
          disabled={disabled}
        />
      </div>
    </div>
  )
);

// Add a display name for easier debugging in React DevTools
CustomInput.displayName = "CustomInput";

const AuctionDateInput = ({
  heading,
  value,
  onChange,
  minDate,
  maxDate,
  error,
  disabled,
  readOnly = false,
  height = 40,
}) => {
  // RESTRUCTURE 2: Remove unused 'calendarOpen' state
  // const [calendarOpen, setCalendarOpen] = useState(false);

  /**
   * RESTRUCTURE 3: Normalize min/max dates using useMemo.
   * This fixes the "blur" issue by ensuring the time component is set
   * to the very start (minDate) or end (maxDate) of the day.
   */
  const normalizedMinDate = useMemo(() => {
    if (!minDate) return undefined;
    try {
      const date = new Date(minDate);
      if (isNaN(date.getTime())) {
        console.error("Invalid minDate provided:", minDate);
        return undefined;
      }
      date.setHours(0, 0, 0, 0); // Set to midnight (start of day)
      return date;
    } catch {
      return undefined;
    }
  }, [minDate]);

  const normalizedMaxDate = useMemo(() => {
    if (!maxDate) return undefined;
    try {
      const date = new Date(maxDate);
      if (isNaN(date.getTime())) {
        console.error("Invalid maxDate provided:", maxDate);
        return undefined;
      }
      date.setHours(23, 59, 59, 999); // Set to end of day
      return date;
    } catch {
      return undefined;
    }
  }, [maxDate]);

  /**
   * RESTRUCTURE 4: Memoize the complex popperProps object.
   * This prevents it from being recreated on every component render.
   */
  const popperProps = useMemo(
    () => ({
      strategy: "fixed", // 'fixed' is good for z-index in portals
      modifiers: [
        {
          name: "zIndex",
          enabled: true,
          phase: "write",
          fn: ({ state }) => {
            // Use optional chaining for cleaner, safer access
            if (state?.styles?.popper) {
              state.styles.popper.zIndex = 2000;
            }
            return state;
          },
        },
      ],
    }),
    [] // Empty array means this object is created only once
  );

  return (
    <div className="inputDateContainer" style={{ position: "relative" }}>
      {heading && <div className="inputextElem">{heading}</div>}
      <div
        className="date-picker-container"
      >
        <DatePicker
          selected={value}
          onChange={(date) => onChange(date)}
          placeholderText="DD-MM-YYYY"
          customInput={
            <CustomInput
              disabled={disabled}
              readOnly={readOnly}
              elemHeight={height}
              placeholder="DD-MM-YYYY"
            />
          }
          dateFormat="dd-MM-yyyy"
          showYearDropdown
          showMonthDropdown
          scrollableMonthYearDropdown
          minDate={normalizedMinDate} // Use normalized date
          maxDate={normalizedMaxDate} // Use normalized date
          popperPlacement="top-start"
          popperProps={popperProps} // Use memoized props
          disabled={disabled}
        />
      </div>
      {error && <div className="dateErrorHeading">{error}</div>}
    </div>
  );
};

export default AuctionDateInput;
